/**
 * Search configuration and limits for OpenSearch queries
 */

export const SEARCH_CONFIG = {
  limits: {
    size: {
      default: 24, // List view pagination
      max: 500, // Reasonable max for list views
      mapMax: 1000, // Map clustering handles more markers efficiently
      mapDefault: 100, // Balance between coverage and performance
    },
    offset: {
      max: 1000, // Matches max_result_window in index settings
      safe: 500, // Practical UX limit (20+ pages @ size=24)
      recommended: 250, // Log warnings above this
    },
    query: {
      maxLength: 200,
      maxWordLength: 15, // Disable fuzziness for words longer than this
    },
    geo: {
      defaultRadiusKm: 25, // Default radius for discovery searches
      maxRadiusKm: 500, // Max radius for geo_distance queries (prevent global scans)
    },
  },
} as const;

export interface ValidationResult {
  size: number;
  from: number;
  warnings: string[];
}

/**
 * Validates and clamps pagination parameters to safe limits
 */
export function validatePaginationParams(
  size: number,
  from: number,
  context: "list" | "map" = "list",
): ValidationResult {
  const warnings: string[] = [];

  const maxSize =
    context === "map"
      ? SEARCH_CONFIG.limits.size.mapMax
      : SEARCH_CONFIG.limits.size.max;

  // OpenSearch rejects any page that ends past max_result_window
  const resultWindow = SEARCH_CONFIG.limits.offset.max;

  const safeFrom = Math.max(0, Math.min(from, resultWindow - 1));
  if (from !== safeFrom) {
    warnings.push(`Offset ${from} clamped to ${safeFrom}`);
  }

  const safeSize = Math.max(
    1,
    Math.min(size, maxSize, resultWindow - safeFrom),
  );
  if (size !== safeSize) {
    warnings.push(`Size ${size} clamped to ${safeSize}`);
  }

  // Warn on deep pagination (unusual usage pattern)
  if (safeFrom > SEARCH_CONFIG.limits.offset.safe) {
    warnings.push(
      `Deep pagination detected: from=${safeFrom}, size=${safeSize}`,
    );
  }

  // Warn on very large result sets (potential abuse)
  if (safeSize > 200) {
    warnings.push(`Large result set requested: size=${safeSize}`);
  }

  return { size: safeSize, from: safeFrom, warnings };
}

/**
 * Offset of the next page, or null when the results end or the next page
 * would start outside the OpenSearch result window.
 */
export function getNextOffset(offset: number, total: number): number | null {
  return offset < Math.min(total, SEARCH_CONFIG.limits.offset.max)
    ? offset
    : null;
}
